import services from "../../views/Orders/services";

export type InvoiceInfo = {
  balanceAmount?: number;
  invoiceMediaId: string;
  invoiceNo: string;
  invoiceAmount: string;
  invoiceDate: string;
  mrpInvoiceMediaId: string;
};

export type OrderItem = {
  partId: string;
  rootPartNumber: string;
  orderedQuantity: number;
  [key: string]: any;
};

export type SupplierParts = {
  rootPartNumber: string;
  familyQuantity: number;
  [key: string]: any;
};

export type OrderItemDiff = {
  fulfilledOrderItems: OrderItem[];
  remainingOrderItems: OrderItem[];
};

type SortableOrderItem = {
  remainingDecisionQuantity?: number;
  holdQuantity?: number;
};

export function sortOrderItemsByDecisionPriority<T extends SortableOrderItem>(
  items: T[] = [],
): T[] {
  return [...items].sort(
    (first, second) =>
      Number(second?.remainingDecisionQuantity || 0) +
      Number(second?.holdQuantity || 0) -
      (Number(first?.remainingDecisionQuantity || 0) +
        Number(first?.holdQuantity || 0)),
  );
}

export function splitOrderItemsBySupplierInventory(
  orderItems: OrderItem[],
  supplierParts: SupplierParts[],
): OrderItemDiff {
  const supplierInventoryMap = new Map<string, number>();
  supplierParts.forEach((supplierPart) => {
    supplierInventoryMap.set(
      supplierPart.rootPartNumber,
      supplierPart.familyQuantity,
    );
  });

  const fulfilledOrderItems: OrderItem[] = [];
  const remainingOrderItems: OrderItem[] = [];

  for (const item of orderItems) {
    const requestedQuantity = item.remainingDecisionQuantity;
    const availableQuantity =
      supplierInventoryMap.get(item.rootPartNumber) ?? 0;

    const fulfilledQuantity = Math.min(requestedQuantity, availableQuantity);
    const remainingQuantity = Math.max(
      0,
      requestedQuantity - availableQuantity,
    );

    fulfilledOrderItems.push({
      ...item,
      orderedQuantity: fulfilledQuantity,
      remainingDecisionQuantity: fulfilledQuantity,
      holdQuantity: 0,
      cancelledQuantity: 0,
    });

    remainingOrderItems.push({
      ...item,
      orderedQuantity: remainingQuantity,
      remainingDecisionQuantity: remainingQuantity,
      holdQuantity: 0,
      cancelledQuantity: 0,
    });
  }

  return {
    fulfilledOrderItems: fulfilledOrderItems.filter(
      (item) => item.orderedQuantity !== 0,
    ),
    remainingOrderItems: remainingOrderItems.filter(
      (item) => item.orderedQuantity !== 0,
    ),
  };
}

export type OrderMetaInfo = {
  orderHandlerAllocationTime: string;
  [key: string]: any;
};

export async function downloadInvoiceForOrder(invoiceMediaId: any) {
  try {
    const mediaResponse = await services.getMediaData(invoiceMediaId);
    window.open(mediaResponse.path, "_blank");
  } catch (error) {
    alert("Invoice is not available");
  }
}

/**
 * Backend sometimes sends `invoiceMediaId` as a string, sometimes as Mongo JSON `{ $oid: "..." }`.
 * This returns one normalized string id, or undefined if missing/empty.
 */
export function resolveInvoiceMediaId(
  invoiceMediaId: unknown,
): string | undefined {
  if (invoiceMediaId == null) return undefined;

  if (typeof invoiceMediaId === "string") {
    const trimmed = invoiceMediaId.trim();
    return trimmed !== "" ? trimmed : undefined;
  }

  if (typeof invoiceMediaId === "object") {
    const mongoObjectId = (invoiceMediaId as { $oid?: unknown }).$oid;
    if (typeof mongoObjectId === "string") {
      const trimmed = mongoObjectId.trim();
      return trimmed !== "" ? trimmed : undefined;
    }
  }

  return undefined;
}

/** True when the order document has uploaded Garaaz (single) invoice PDF/media. */
export function hasGaraazInvoiceUploaded(orderData: {
  invoiceInfo?: { invoiceMediaId?: unknown } | null;
}): boolean {
  return !!resolveInvoiceMediaId(orderData?.invoiceInfo?.invoiceMediaId);
}

/**
 * True when the active supplier allocation has submitted invoice metadata:
 * a non-empty invoice number and/or an uploaded invoice PDF/media id.
 */
export function hasSupplierInvoice(invoiceInfo: unknown): boolean {
  if (!invoiceInfo || typeof invoiceInfo !== "object") return false;
  const invoiceFields = invoiceInfo as Record<string, unknown>;
  const supplierInvoiceNumber = invoiceFields.invoiceNo;
  const hasSupplierInvoiceNumber =
    typeof supplierInvoiceNumber === "string" &&
    supplierInvoiceNumber.trim().length > 0;
  const hasSupplierInvoiceMediaAttachment =
    !!resolveInvoiceMediaId(invoiceFields.invoiceMediaId);
  return hasSupplierInvoiceNumber || hasSupplierInvoiceMediaAttachment;
}

export function getEnquiryStatus(
  orderItems: any[],
  orderStatus: string,
): "OPEN" | "CLOSED" {
  if (orderStatus === "CANCELLED" || orderStatus === "ENQUIRY_CLOSED") {
    return "CLOSED";
  }
  return "OPEN";
}
